import { getEthUsdPrice, getUsdPriceForSymbol } from './prices';

const ERC20_ABI = [
  'function balanceOf(address) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
];

const BELT_VAULT_ABI = [
  'function balanceOf(address) view returns (uint256)',
  'function convertToAssets(uint256 shares) view returns (uint256)',
  'function totalAssets() view returns (uint256)',
  'function totalSupply() view returns (uint256)',
];

const CHAIN_IDS = {
  ethereum: 1,
  arbitrum: 42161,
};

function getEthers() {
  const eth = window?.ethers;
  if (!eth) {
    throw new Error('ethers_unavailable');
  }
  return eth;
}

function getConfig() {
  return window.CONFIG || {};
}

function safeNumber(value) {
  return Number.isFinite(value) ? value : 0;
}

function makePosition({ id, chain, bucket, label, symbol, amount, usdValue, priced, meta }) {
  return {
    id,
    chain,
    bucket,
    label,
    underlyingSymbol: symbol,
    underlyingAmount: amount,
    usdValue: priced ? usdValue : undefined,
    priced: Boolean(priced),
    meta,
  };
}

async function getErc20Balance(provider, address, holder) {
  const ethers = getEthers();
  const token = new ethers.Contract(address, ERC20_ABI, provider);
  const [bal, decimals, symbol] = await Promise.all([
    token.balanceOf(holder),
    token.decimals().catch(() => 18),
    token.symbol().catch(() => ''),
  ]);
  const amount = Number(ethers.formatUnits(bal, decimals));
  return { amount, symbol };
}

async function getNativeBalance(provider, holder) {
  const ethers = getEthers();
  const bal = await provider.getBalance(holder);
  return Number(ethers.formatUnits(bal, 18));
}

function getRpcProvider(chainId) {
  const ethers = getEthers();
  const config = getConfig();
  const rpc = config?.RPC?.[chainId];
  if (!rpc) {
    throw new Error(`rpc_missing_${chainId}`);
  }
  return new ethers.JsonRpcProvider(rpc, chainId);
}

function getWalletProvider() {
  const ethers = getEthers();
  if (!window?.ethereum) return null;
  return new ethers.BrowserProvider(window.ethereum, 'any');
}

async function getWalletChainId() {
  try {
    if (!window?.ethereum?.request) return null;
    const raw = await window.ethereum.request({ method: 'eth_chainId' });
    if (typeof raw === 'string' && raw.startsWith('0x')) {
      return parseInt(raw, 16);
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function isZeroAddress(address) {
  return /^0x0{40}$/i.test(String(address || ''));
}

async function fetchWalletPositions({
  provider,
  chain,
  chainId,
  userAddress,
  ethPrice,
}) {
  const config = getConfig();
  const walletAssets = config?.WALLET_ASSETS?.[chainId] || window.WALLET_ASSETS?.[chainId] || [];
  const positions = [];

  const nativeBalance = await getNativeBalance(provider, userAddress).catch(() => 0);
  if (nativeBalance > 0) {
    const usdPrice = getUsdPriceForSymbol('ETH', ethPrice);
    const priced = Number.isFinite(usdPrice) && usdPrice > 0;
    const usdValue = priced ? nativeBalance * usdPrice : undefined;
    positions.push(
      makePosition({
        id: `wallet:${chain}:ETH`,
        chain,
        bucket: 'wallet',
        label: 'Wallet ETH',
        symbol: 'ETH',
        amount: nativeBalance,
        usdValue,
        priced,
      })
    );
  }

  for (const asset of walletAssets) {
    if (!asset?.address || !asset?.symbol) continue;
    const symbol = asset.symbol;
    if (symbol === 'wstETH') {
      // Track wstETH under the Lido bucket to avoid double-counting in Wallet.
      continue;
    }
    try {
      const { amount, symbol: onchainSymbol } = await getErc20Balance(
        provider,
        asset.address,
        userAddress
      );
      if (!amount || amount <= 0) continue;
      const tokenSymbol = onchainSymbol || symbol;
      const usdPrice = getUsdPriceForSymbol(tokenSymbol, ethPrice);
      const priced = Number.isFinite(usdPrice) && usdPrice > 0;
      const usdValue = priced ? amount * usdPrice : undefined;
      positions.push(
        makePosition({
          id: `wallet:${chain}:${tokenSymbol}`,
          chain,
          bucket: 'wallet',
          label: `Wallet ${tokenSymbol}`,
          symbol: tokenSymbol,
          amount,
          usdValue,
          priced,
        })
      );
    } catch {
      // ignore unreadable tokens
    }
  }

  return positions;
}

async function fetchAavePositions({ provider, chain, chainId, userAddress, ethPrice }) {
  const config = getConfig();
  const aTokens = config?.AAVE?.ATOKENS || {};
  const positions = [];

  const entries = Object.entries(aTokens);
  for (const [sym, addr] of entries) {
    if (!addr) continue;
    try {
      const { amount } = await getErc20Balance(provider, addr, userAddress);
      if (!amount || amount <= 0) continue;
      const underlying = sym.replace(/^a/i, '');
      const usdPrice = getUsdPriceForSymbol(underlying, ethPrice);
      const priced = Number.isFinite(usdPrice) && usdPrice > 0 && underlying === 'USDC';
      const usdValue = priced ? amount * usdPrice : undefined;
      positions.push(
        makePosition({
          id: `aave:${chain}:${sym}`,
          chain,
          bucket: 'aave',
          label: `Aave ${sym}`,
          symbol: underlying,
          amount,
          usdValue,
          priced,
        })
      );
    } catch {
      // ignore
    }
  }

  return positions;
}

async function fetchLidoPositions({ provider, chain, chainId, userAddress }) {
  const config = getConfig();
  const positions = [];
  const lido = config?.LIDO?.[chainId] || {};
  const wstEthAddress = lido?.WSTETH;

  if (wstEthAddress && !isZeroAddress(wstEthAddress)) {
    try {
      const { amount } = await getErc20Balance(provider, wstEthAddress, userAddress);
      if (amount > 0) {
        positions.push(
          makePosition({
            id: 'lido:ethereum:wstETH',
            chain,
            bucket: 'lido',
            label: 'Lido wstETH',
            symbol: 'wstETH',
            amount,
            usdValue: undefined,
            priced: false,
          })
        );
      }
    } catch {
      // ignore
    }
  }

  return positions;
}

async function fetchBeltPositions({ provider, chain, userAddress }) {
  const config = getConfig();
  const belts = config?.BELT_VAULTS || [];
  const positions = [];
  if (!belts.length) return positions;

  const ethers = getEthers();

  for (const belt of belts) {
    if (belt.chain !== chain) continue;
    try {
      const vault = new ethers.Contract(belt.address, BELT_VAULT_ABI, provider);
      const shares = await vault.balanceOf(userAddress);
      let assets = 0n;
      try {
        assets = await vault.convertToAssets(shares);
      } catch {
        const [totalAssets, totalSupply] = await Promise.all([
          vault.totalAssets(),
          vault.totalSupply(),
        ]);
        assets = totalSupply > 0n ? (shares * totalAssets) / totalSupply : 0n;
      }
      const amount = Number(ethers.formatUnits(assets, 6));
      if (!amount || amount <= 0) continue;
      positions.push(
        makePosition({
          id: `belt:${belt.key}`,
          chain,
          bucket: 'belts',
          label: belt.label || `Belt ${belt.key}`,
          symbol: 'USDC',
          amount,
          usdValue: amount,
          priced: true,
          meta: { vault: belt.address },
        })
      );
    } catch (err) {
      positions.push(
        makePosition({
          id: `belt:${belt.key}`,
          chain,
          bucket: 'belts',
          label: belt.label || `Belt ${belt.key}`,
          symbol: 'USDC',
          amount: 0,
          usdValue: undefined,
          priced: false,
          meta: { vault: belt.address, error: err?.message || 'belt_read_failed' },
        })
      );
    }
  }

  return positions;
}

export async function buildPortfolioSnapshot({ userAddress }) {
  const warnings = [];
  const positions = [];

  let ethPrice = 0;
  try {
    ethPrice = await getEthUsdPrice();
  } catch {
    warnings.push('ETH price unavailable; ETH/stETH positions unpriced.');
  }

  const walletProvider = getWalletProvider();
  const walletChainId = await getWalletChainId();

  const chainTasks = [
    {
      chain: 'ethereum',
      chainId: CHAIN_IDS.ethereum,
      tasks: async () => {
        const provider = walletProvider && walletChainId === CHAIN_IDS.ethereum
          ? walletProvider
          : getRpcProvider(CHAIN_IDS.ethereum);
        const wallet = await fetchWalletPositions({
          provider,
          chain: 'ethereum',
          chainId: CHAIN_IDS.ethereum,
          userAddress,
          ethPrice,
        });
        const aave = await fetchAavePositions({
          provider,
          chain: 'ethereum',
          chainId: CHAIN_IDS.ethereum,
          userAddress,
          ethPrice,
        });
        const lido = await fetchLidoPositions({
          provider,
          chain: 'ethereum',
          chainId: CHAIN_IDS.ethereum,
          userAddress,
        });
        return [...wallet, ...aave, ...lido];
      },
    },
    {
      chain: 'arbitrum',
      chainId: CHAIN_IDS.arbitrum,
      tasks: async () => {
        const provider = walletProvider && walletChainId === CHAIN_IDS.arbitrum
          ? walletProvider
          : getRpcProvider(CHAIN_IDS.arbitrum);
        const wallet = await fetchWalletPositions({
          provider,
          chain: 'arbitrum',
          chainId: CHAIN_IDS.arbitrum,
          userAddress,
          ethPrice,
        });
        const belts = await fetchBeltPositions({
          provider,
          chain: 'arbitrum',
          userAddress,
        });
        const lido = await fetchLidoPositions({
          provider,
          chain: 'arbitrum',
          chainId: CHAIN_IDS.arbitrum,
          userAddress,
        });
        return [...wallet, ...belts, ...lido];
      },
    },
  ];

  await Promise.all(
    chainTasks.map(async ({ chain, tasks }) => {
      try {
        const list = await tasks();
        positions.push(...list);
      } catch (err) {
        warnings.push(`${chain} RPC unavailable; partial data hidden.`);
      }
    })
  );

  const totals = {
    totalUsd: 0,
    perChainUsd: { ethereum: 0, arbitrum: 0 },
    perBucketUsd: { wallet: 0, aave: 0, lido: 0, belts: 0 },
  };

  for (const pos of positions) {
    if (!pos.priced || !Number.isFinite(pos.usdValue)) continue;
    totals.totalUsd += pos.usdValue;
    totals.perChainUsd[pos.chain] =
      safeNumber(totals.perChainUsd[pos.chain]) + pos.usdValue;
    totals.perBucketUsd[pos.bucket] =
      safeNumber(totals.perBucketUsd[pos.bucket]) + pos.usdValue;
  }

  const unpriced = positions.filter((pos) => !pos.priced);

  return {
    snapshot: {
      ts: Date.now(),
      totalUsd: totals.totalUsd,
      perChainUsd: totals.perChainUsd,
      perBucketUsd: totals.perBucketUsd,
      positions,
      unpriced,
    },
    warnings,
  };
}
